import { useI18n } from '../i18n';
import type { StyleCount } from '../api/types';

interface Props {
  /** Estilos que existem nesta skin, com a contagem de cada um. */
  disponiveis: StyleCount[];
  selecionados: string[];
  /**
   * A contagem pode estar incompleta.
   *
   * O normal é ela ser a da skin inteira: o site lê todas as páginas de
   * anúncios antes de contar. Só quando a skin passa do teto de páginas é que
   * sobra anúncio sem ler — aí o número vira "26+", que é o que se pode
   * afirmar de verdade.
   */
  parcial?: boolean;
  onChange: (styles: string[]) => void;
}

/**
 * Filtro de estilo (fase de Doppler, faixa de Fade).
 *
 * As opções vêm do que existe de verdade nos anúncios da skin — oferecer
 * "Rubi" numa faca sem fase nenhuma só produziria lista vazia. Por isso o
 * componente some quando a skin não tem estilo.
 *
 * A contagem é de todos os anúncios da skin, e não da página aberta: é a
 * pergunta que o cliente faz ("quantas Fase 4 tem essa faca?").
 */
export function StyleFilter({ disponiveis, selecionados, parcial, onChange }: Props) {
  const { lang } = useI18n();
  if (disponiveis.length === 0) return null;

  function alternar(key: string) {
    onChange(selecionados.includes(key) ? selecionados.filter((k) => k !== key) : [...selecionados, key]);
  }

  return (
    <div className="style-filter">
      <span className="style-label">{lang === 'pt' ? 'Estilo' : 'Style'}</span>
      {disponiveis.map((s) => {
        const ativo = selecionados.includes(s.key);
        return (
          <button
            key={s.key}
            className={`style-chip ${ativo ? 'active' : ''}`}
            onClick={() => alternar(s.key)}
            title={
              parcial
                ? lang === 'pt'
                  ? `ao menos ${s.count} — a skin tem anúncios demais para varrer inteira`
                  : `at least ${s.count} — this skin has too many listings to scan in full`
                : `${s.count} ${lang === 'pt' ? 'anúncio(s)' : 'listing(s)'}`
            }
            aria-pressed={ativo}
          >
            <i style={{ background: s.color }} aria-hidden />
            {lang === 'pt' ? s.pt : s.en}
            <b>
              {s.count}
              {parcial ? '+' : ''}
            </b>
          </button>
        );
      })}
      {selecionados.length > 0 && (
        <button className="style-clear" onClick={() => onChange([])}>
          {lang === 'pt' ? 'limpar' : 'clear'}
        </button>
      )}
    </div>
  );
}
